import React from 'react';
import { motion } from 'framer-motion';
import { BeakerIcon, LightBulbIcon, ArchiveBoxIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

const Hydroponics = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-cyan-900/60 mix-blend-multiply z-10"></div>
                    <img
                        src={require('../../../assets/images/services/hydroponics.png')}
                        alt="Hydroponics"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-thin mb-6"
                    >
                        Future of <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Farming</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Soil-Free Farming. Limitless Possibilities.
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-2/3"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            <span className="font-semibold text-gray-900">Hydroponics</span> at Marvelous Agri Solution is the future of controlled, high-efficiency agriculture.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            We grow crops without soil — using precisely balanced nutrient solutions, optimized water systems, and climate-controlled environments to deliver faster growth, higher yield, and superior quality.
                        </p>
                        <p className="text-xl font-medium text-gray-800 italic border-l-4 border-cyan-500 pl-6 py-4 bg-cyan-50 rounded-r-2xl mb-10">
                            This is agriculture redesigned for the modern world.
                        </p>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-cyan-500 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                                🚀 Our Hydroponic System Advantage
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {[
                                    "Up to 90% less water usage",
                                    "Faster crop growth cycles",
                                    "Higher nutrient efficiency",
                                    "Controlled pest exposure",
                                    "Year-round production capability"
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-gray-700">
                                        <CheckBadgeIcon className="h-6 w-6 text-cyan-600 flex-shrink-0" />
                                        <span className="font-medium text-gray-800">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-cyan-800 font-medium italic mt-6 border-t border-gray-100 pt-6">
                                Every plant receives exactly what it needs — no more, no less.
                            </p>
                        </div>

                        <div className="bg-gray-900 p-8 rounded-2xl shadow-xl text-white mb-10">
                            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-cyan-400">
                                🌱 Smart & Sustainable Cultivation
                            </h3>
                            <p className="text-gray-300 text-lg font-light mb-6">
                                Our hydroponic solutions integrate:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                {[
                                    "Precision nutrient formulation",
                                    "Automated irrigation control",
                                    "Climate monitoring systems",
                                    "Growth-stage optimization",
                                    "High-density cultivation models"
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3 text-gray-300 font-light">
                                        <div className="bg-cyan-500/20 p-1.5 rounded-full flex-shrink-0">
                                            <div className="h-2 w-2 rounded-full bg-cyan-400"></div>
                                        </div>
                                        {item}
                                    </div>
                                ))}
                            </div>
                            <div className="bg-cyan-900/50 p-6 rounded-xl border border-cyan-700/50">
                                <p className="text-lg text-cyan-100 font-light mb-2">The result?</p>
                                <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">
                                    Cleaner produce. Consistent quality. Maximum space utilization.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                🌍 Why Hydroponics is the Future
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                                With shrinking land availability and rising climate uncertainty, hydroponics provides:
                            </p>
                            <ul className="space-y-4 mb-6">
                                {[
                                    "Urban farming opportunities",
                                    "Export-quality produce",
                                    "Reduced chemical dependency",
                                    "Predictable production output"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-4 text-gray-600 font-light text-lg">
                                        <div className="bg-cyan-100 p-1.5 rounded-full flex-shrink-0">
                                            <svg className="h-5 w-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xl font-medium italic text-center text-cyan-700 bg-cyan-50 py-3 rounded-lg">
                                It is efficient, scalable, and sustainable.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 p-8 rounded-2xl border border-cyan-200/60 shadow-sm relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-cyan-200/40 rounded-full blur-3xl"></div>
                            <h3 className="text-2xl font-bold text-cyan-900 mb-4 relative z-10">🌿 Farming Beyond Soil</h3>
                            <p className="text-cyan-800 text-lg leading-relaxed font-light mb-6 relative z-10">
                                Hydroponics is not just an alternative method — it is a revolution in agricultural productivity.
                            </p>
                            <p className="text-cyan-800 text-lg leading-relaxed font-light mb-8 relative z-10">
                                At Marvelous Agri Solution, we design intelligent hydroponic systems that deliver performance, purity, and profitability.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-between gap-4 relative z-10 pt-6 border-t border-cyan-200">
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-700 to-blue-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-500"></span> Controlled Environment.
                                </p>
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-700 to-blue-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-500"></span> Calculated Nutrition.
                                </p>
                                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-700 to-blue-600 flex items-center gap-2">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-500"></span> Consistent Results.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/3 sticky top-24"
                    >
                        <div className="bg-white p-2 rounded-2xl shadow-2xl skew-y-2 transform hover:skew-y-0 transition-transform duration-500">
                            <img
                                src={require('../../../assets/images/services/hydroponics.png')}
                                alt="Hydroponics System"
                                className="rounded-xl w-full h-auto object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Hydroponics;
